import { useState } from 'react';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

import Alert from '@/components/Alert';
import Button from '@/components/Button';
import Card from '@/components/Card';
import FormElement from '@/components/FormElement';
import Modal from '@/components/Modal';
import Stack from '@/components/Stack';
import TextArea from '@/components/TextArea';

type Props = {
  onClose: () => void;
  onSubmit: (body: string) => Promise<void>;
};

const AddCommentModal = ({ onClose, onSubmit }: Props) => {
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!body.trim()) return;

    setLoading(true);
    setError(null);

    try {
      await onSubmit(body.trim());
      onClose();
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to add comment';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      disableClickout
      size="sm"
      title="Add Comment"
      onRequestClose={onClose}
    >
      <Card.Section>
        <Stack vertical gap={12}>
          {error && (
            <Alert
              description={error}
              icon={faExclamationTriangle}
              status="danger"
              title="Error"
            />
          )}

          <FormElement label="Comment" required showRequiredIndicator>
            <TextArea
              placeholder="Write a comment..."
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
            />
          </FormElement>
        </Stack>
      </Card.Section>

      <Card.Section>
        <Stack justify="end">
          <Button
            a11yLabel="Cancel adding comment"
            appearance="outline"
            label="Cancel"
            onClick={onClose}
          />
          <Button
            a11yLabel="Save comment"
            isLoading={loading}
            label="Save"
            status="theme"
            onClick={handleSubmit}
            disabled={!body.trim()}
          />
        </Stack>
      </Card.Section>
    </Modal>
  );
};

export default AddCommentModal;
